import Loading from "@/pages/loading";
import { APP_ROUTES, apiErrorParser, useAppTranslation } from "@/utils";
import { bnplConsumerApis } from "@payping/api";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { dateFormats, moment } from "@payping/utils/dateUtils";
import { Button, Card, Divider, Flex, Typography, useMessagedAsync, useMessagedAsyncFn } from "@payping/web-components";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";

const HomeInstallmentDetailsPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useAppTranslation();

  const [paymentState, payment] = useMessagedAsyncFn(
    async (trackingCode: string) => {
      try {
        const res = await bnplConsumerApis.installmentPayment({
          installmentCode: code ?? "",
          trackingCode,
        });
        if (res.url) {
          window.location.replace(res.url);
        }
        return res;
      } catch (error: any) {
        const parsedError = await apiErrorParser(error);
        if (parsedError.parsed) {
          throw parsedError.parsed;
        }
        throw parsedError.original;
      }
    },
    [code],
    { loading: false },
    { error: true },
  );

  const installmentDetailsStatus = useMessagedAsync(
    async () => {
      if (!code) return Promise.reject(new Error("code needed"));
      const res = await bnplConsumerApis.installmentDetail(code ?? "");
      return res;
    },
    [code],
    {
      success: false,
      error: true,
    },
  );

  useEffect(() => {
    if (!code) navigate(APP_ROUTES.home.overview.url);
  }, [code, navigate]);

  const handlePay = async () => {
    const trackingCode = installmentDetailsStatus.value?.trackingCode;
    if (!trackingCode) return;
    await payment(trackingCode);
  };

  if (installmentDetailsStatus.loading || installmentDetailsStatus.error) {
    return <Loading />;
  }

  return (
    <Flex vertical className="tw:h-full">
      <Flex vertical className="tw:px-4! tw:py-8!" gap={"middle"}>
        <Typography.Title level={5} className="tw:m-0!">
          {t("common.pay")}
        </Typography.Title>
        <Card size="small">
          <Flex align="center" vertical className="tw:w-full" gap={"middle"}>
            <Flex className="tw:w-full">
              <Typography.Text>
                {t("common.installmentPurchaseFrom", { name: installmentDetailsStatus.value?.merchantName ?? "" })}
              </Typography.Text>
            </Flex>

            <Flex justify="space-between" align="center" className="tw:w-full">
              <Typography.Text type="secondary">{`${t("common.amount")} :`}</Typography.Text>

              <Flex align="center">
                <Typography.Title level={5} className="tw:m-0!">
                  {formatLocalizedNumber({
                    value:
                      (installmentDetailsStatus.value?.installmentAmount ?? 0) -
                      (installmentDetailsStatus.value?.delayPenaltyAmount ?? 0),
                  })}
                </Typography.Title>
                <CuToman width={24} height={24} />
              </Flex>
            </Flex>

            <Flex justify="space-between" align="center" className="tw:w-full">
              <Typography.Text type="secondary">{`${t("common.paymentTitle")}:`}</Typography.Text>
              <Typography.Text>
                {`${t("common.installment")} ${(t("common.countingNumbers", { returnObjects: true }) as string[])[installmentDetailsStatus.value?.number ?? 0]}`}
              </Typography.Text>
            </Flex>

            <Flex justify="space-between" align="center" className="tw:w-full">
              <Typography.Text type="secondary">{`${t("common.dueDate")} :`}</Typography.Text>
              <Typography.Text>
                {installmentDetailsStatus.value?.dueDate
                  ? moment(installmentDetailsStatus.value?.dueDate).format(dateFormats.ui.jalaliDate)
                  : null}
              </Typography.Text>
            </Flex>

            {installmentDetailsStatus.value?.delayPenaltyAmount ? (
              <Flex justify="space-between" align="center" className="tw:w-full">
                <Typography.Text type="secondary">{`${t("common.delayedPenalty")} :`}</Typography.Text>

                <Flex align="center">
                  <Typography.Title level={5} className="tw:m-0!">
                    {formatLocalizedNumber({ value: installmentDetailsStatus.value?.delayPenaltyAmount ?? 0 })}
                  </Typography.Title>
                  <CuToman width={24} height={24} />
                </Flex>
              </Flex>
            ) : null}
          </Flex>
        </Card>
      </Flex>

      <Flex vertical className="tw:mt-auto tw:w-full">
        <Divider className="tw:m-0!" />
        <Flex className="tw:w-full tw:px-4! tw:py-2!" align="center" justify="space-between">
          <Flex vertical>
            <Typography.Text type="secondary">{t("common.payableAmount")}</Typography.Text>

            <Flex align="center">
              <Typography.Text>
                {formatLocalizedNumber({ value: installmentDetailsStatus.value?.installmentAmount ?? 0 })}
              </Typography.Text>
              <CuToman width={24} height={24} />
            </Flex>
          </Flex>
          {installmentDetailsStatus.value?.isPayable ? (
            <Button className="tw:px-10!" type="primary" onClick={handlePay} loading={paymentState.loading}>
              {t("common.pay")}
            </Button>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default HomeInstallmentDetailsPage;
